import { useEffect, useRef, useState } from 'react';

/**
 * Returns the `id` of the section element currently most in view,
 * accounting for sticky-nav height via a negative top rootMargin.
 *
 * Implementation uses IntersectionObserver with a top margin that
 * subtracts the nav height so "in view" means "visible below the nav".
 * The active section is whichever observed element entered the viewport
 * most recently; on initial load the topmost visible section wins.
 *
 * @param sectionIds - Array of DOM element `id` values to observe.
 * @param options.offsetPx - How many px of negative top margin to apply
 *   (equal to the sticky nav height). Default: 80.
 * @returns The currently active section id, or `null` if none are visible.
 */
export function useScrollSpy(sectionIds: string[], options?: { offsetPx?: number }): string | null {
  const offsetPx = options?.offsetPx ?? 80;
  const [activeId, setActiveId] = useState<string | null>(null);

  // Keep a stable ref to the section-ids array so the effect doesn't
  // re-run on every render if the caller passes an inline array literal.
  const idsRef = useRef(sectionIds);
  useEffect(() => {
    idsRef.current = sectionIds;
  });

  useEffect(() => {
    const ids = idsRef.current;

    // Track which sections are currently intersecting. When the set
    // changes, set the active id to the element that appears first in
    // the DOM (lowest index in the ids array).
    const intersecting = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            intersecting.add(entry.target.id);
          } else {
            intersecting.delete(entry.target.id);
          }
        }

        // Prefer the section that appears earliest in the nav order.
        const firstVisible = ids.find((id) => intersecting.has(id));
        setActiveId(firstVisible ?? null);
      },
      {
        // Negative top margin = sections only count as "intersecting"
        // once they appear below the sticky nav. The bottom margin is
        // generous so sections that take up most of the viewport register.
        rootMargin: `-${String(offsetPx)}px 0px -40% 0px`,
        threshold: 0,
      }
    );

    // Observe all matching elements; silently skip missing ids (section
    // may not be in the DOM on this page or during SSR hydration).
    const elements: Element[] = [];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        elements.push(el);
      }
    }

    return () => {
      for (const el of elements) {
        observer.unobserve(el);
      }
      observer.disconnect();
    };
  }, [offsetPx]);

  return activeId;
}
